// react
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui
import { Dialog, DialogContent, DialogTitle } from '@mui/material';

// api
import type { GetEnergyTreeNodeDto } from '@/api/generated/recalc/endpoints/v2/energyGroups';
import {
  useGetEnergyGroupQuery,
  usePutEnergyGroupEnergyTreeNodeMutation,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
// shared-components
import { WritableOnly } from '@/components/access-control';

// feature-utils
import { getInitiativeInclusionsForNode } from '@/features/energyxplorer/utils/energyTreeNodeInitiativeInclusion';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import MoveNodeForm from '../../forms/MoveNodeForm';
import { toMoveNodePayload } from '../../forms/moveNodeForm.mappers';
import type { MoveNodeFormValues } from '../../forms/moveNodeForm.schema';
import {
  buildTreeData,
  isNodeDescendant,
} from '../../utils/energyTreeFunctions';

interface TreeNode extends GetEnergyTreeNodeDto {
  _path?: GetEnergyTreeNodeDto[];
  _hoverTimeout?: number;
}

interface Unit {
  UnitId: number;
  Name: string;
  Tags?: Array<{ TagId: number; Text: string }>;
  UnitSensors?: Array<{ Sensor?: { SensorId: number } }>;
  usedInNode?: string;
  notSupportedReason?: string;
}

type MoveNodeDialogProps = {
  open: boolean;
  onClose: () => void;
  tree: GetEnergyTreeNodeDto[];
  energyTreeNode?: GetEnergyTreeNodeDto;
  units?: Unit[]; // Not used in move dialog, but passed by OrgChart for consistency
  mode?: 'server' | 'local';
  onConfirm?: (payload: { energyTreeNodeId: string; parentId: string }) => void;
};

const MoveNodeDialog = ({
  open,
  onClose,
  tree,
  energyTreeNode,
  mode = 'server',
  onConfirm,
}: MoveNodeDialogProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['energyxplorer-tree-tab', 'validation']);
  const { energyGroupId } = useParams({ strict: false }) as {
    energyGroupId?: string;
  };

  const parsedEnergyGroupId = energyGroupId
    ? parseInt(energyGroupId, 10)
    : undefined;

  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: parsedEnergyGroupId! },
    {
      skip: !parsedEnergyGroupId,
    },
  );

  const [hoveredPath, setHoveredPath] = useState<GetEnergyTreeNodeDto[]>([]);

  const [putEnergyTreeNode, { isLoading }] =
    usePutEnergyGroupEnergyTreeNodeMutation();

  const inclusions = useMemo(() => {
    if (!selectedEnergyGroup) return [];
    const nodesFound = getInitiativeInclusionsForNode(
      energyTreeNode?.energyTreeNodeId ?? '',
      selectedEnergyGroup,
    );
    return nodesFound;
  }, [energyTreeNode?.energyTreeNodeId, selectedEnergyGroup]);

  const virtualInitiativeInclusions = useMemo(
    () =>
      (inclusions || []).filter(({ viaNode }) => {
        if (!viaNode || typeof viaNode !== 'object') return false;
        const node = viaNode as { dataStreams?: unknown[] };
        return !node.dataStreams || node.dataStreams.length === 0;
      }),
    [inclusions],
  );

  // Build options excluding current node and its descendants
  const treeOptions = useMemo(() => {
    if (!tree?.length || !energyTreeNode?.energyTreeNodeId) return [];

    const filtered = tree.filter(
      (n) =>
        n.energyTreeNodeId !== energyTreeNode.energyTreeNodeId &&
        !isNodeDescendant(
          n.energyTreeNodeId,
          energyTreeNode.energyTreeNodeId,
          tree,
        ),
    );

    return buildTreeData(filtered) as TreeNode[];
  }, [tree, energyTreeNode]);

  const handleSubmit = async (values: MoveNodeFormValues) => {
    if (!energyTreeNode) return;

    const { parentId } = toMoveNodePayload(values);

    // ✅ LOCAL MODE
    if (mode === 'local') {
      if (typeof onConfirm !== 'function') {
        dispatch(
          showSnackbar({
            message: t('shared_dialog.local_mode_handler_missing', {
              ns: 'energyxplorer-tree-tab',
            }),
            severity: 'error',
          }),
        );
        return;
      }

      onConfirm({
        energyTreeNodeId: energyTreeNode.energyTreeNodeId ?? '',
        parentId,
      });

      dispatch(
        showSnackbar({
          message: t('move_node_dialog.node_moved_success', {
            ns: 'energyxplorer-tree-tab',
          }),
          severity: 'success',
        }),
      );
      onClose();
      return;
    }

    // ✅ SERVER MODE (existing behavior)
    if (!parsedEnergyGroupId) return;

    const payload = { ...energyTreeNode, parentId };

    const result = await putEnergyTreeNode({
      groupId: parsedEnergyGroupId,
      nodeId: energyTreeNode.energyTreeNodeId ?? '',
      putEnergyTreeNodeDto: payload,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('move_node_dialog.node_moved_success', {
            ns: 'energyxplorer-tree-tab',
          }),
          severity: 'success',
        }),
      );
      onClose();
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
  };

  if (!energyTreeNode) return null;

  return (
    <Dialog
      data-testid="orgchart-move-node-dialog"
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        {t('move_node_dialog.title', {
          item: energyTreeNode.name,
          ns: 'energyxplorer-tree-tab',
        })}
      </DialogTitle>
      <WritableOnly>
        <DialogContent dividers>
          <MoveNodeForm
            energyTreeNode={energyTreeNode}
            tree={tree}
            treeOptions={treeOptions}
            virtualInitiativeInclusions={virtualInitiativeInclusions}
            hoveredPath={hoveredPath}
            setHoveredPath={setHoveredPath}
            onSubmit={handleSubmit}
            onCancel={onClose}
            isSubmitting={isLoading}
          />
        </DialogContent>
      </WritableOnly>
    </Dialog>
  );
};

export default MoveNodeDialog;
